import { useQuery } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { backendService } from "../../../../services/backend.service";
import type {
	IntegrationEmployeesSummary,
	IntegrationShiftsSummary,
	IntegrationTimeOffSummary,
	MissingRecurringPatternsSummary,
} from "../../../../utils/models";

const SUMMARY_STALE_TIME_MS = 30_000;

export const useIntegrationEmployeesSummary = (departmentId: string, startDate: DateTime, endDate: DateTime, enabled: boolean) =>
	useQuery<IntegrationEmployeesSummary>({
		queryKey: ["integration-summary", "employees", departmentId, startDate.toISODate(), endDate.toISODate()],
		queryFn: async () => (await backendService.getIntegrationEmployeesSummary(departmentId, startDate, endDate)).data,
		enabled,
		staleTime: SUMMARY_STALE_TIME_MS,
	});

export const useIntegrationTimeOffSummary = (departmentId: string, startDate: DateTime, endDate: DateTime, enabled: boolean) =>
	useQuery<IntegrationTimeOffSummary>({
		queryKey: ["integration-summary", "time-off", departmentId, startDate.toISODate(), endDate.toISODate()],
		queryFn: async () => (await backendService.getIntegrationTimeOffSummary(departmentId, startDate, endDate)).data,
		enabled,
		staleTime: SUMMARY_STALE_TIME_MS,
	});

export const useIntegrationShiftsSummary = (departmentId: string, startDate: DateTime, endDate: DateTime, enabled: boolean) =>
	useQuery<IntegrationShiftsSummary>({
		queryKey: ["integration-summary", "shifts", departmentId, startDate.toISODate(), endDate.toISODate()],
		queryFn: async () => (await backendService.getIntegrationShiftsSummary(departmentId, startDate, endDate)).data,
		enabled,
		staleTime: SUMMARY_STALE_TIME_MS,
	});

// Not keyed by the date range: missing recurring patterns are a current-state check over the
// department's whole recent schedule, so changing the window must not refetch it.
export const useIntegrationShiftsRecurringPatterns = (departmentId: string, enabled: boolean) =>
	useQuery<MissingRecurringPatternsSummary>({
		queryKey: ["integration-summary", "shifts-recurring-patterns", departmentId],
		queryFn: async () => (await backendService.getIntegrationShiftsRecurringPatterns(departmentId)).data,
		enabled,
		staleTime: SUMMARY_STALE_TIME_MS,
	});
